/*
 * Time tracking shapes, mirroring `caseblink/datastore/models_time.py` and the
 * payloads in `caseblink/time_tracking/payloads.py`.
 */

export type EntryStatus =
  | "needs_case"
  | "suggested"
  | "draft"
  | "released"
  | "returned"
  | "approved"
  | "exported"
  | "discarded";

export const UNRELEASED_STATUSES: ReadonlySet<EntryStatus> = new Set([
  "needs_case",
  "suggested",
  "draft",
  "returned"
]);

export type FeeArrangement =
  | "flat_fee"
  | "hourly"
  | "hourly_capped"
  | "non_billable";

export type EntryOrigin = "captured" | "manual" | "assistant" | "review_time";

export type SignalKind =
  | "web_session"
  | "activity"
  | "email"
  | "calendar"
  | "agent";

export type MatchCandidate = {
  individual_id: string;
  foreign_national_id?: string | null;
  label: string;
  reason: string;
  score: number;
};

export type ActivityRow = {
  id: string;
  kind: SignalKind | string;
  title: string;
  detail: string;
  started_at: string;
  ended_at: string;
  active_seconds: number;
  individual_id?: string | null;
  billable: boolean;
  agent_kind?: string | null;
};

export type HistoryItem = {
  at: string;
  by?: string | null;
  by_kind: "user" | "assistant" | "system" | string;
  action: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  note?: string | null;
  thread_id?: string | null;
  change_id?: string | null;
};

export type FlagFixKind = "narrative" | "hours" | "rate_override" | "reviewed";

export type FlagFix = {
  kind: FlagFixKind | string;
  value?: string | number | null;
  label?: string;
};

export type EntryFlag = {
  id: string;
  rule_id: string;
  section?: string | null;
  label: string;
  kind: string;
  severity: "error" | "warn" | "info" | string;
  message: string;
  /** firm | guideline | company (its approved rate) */
  source?: string;
  source_label?: string;
  fix?: FlagFix | null;
  resolution?: "fixed" | "dismissed" | string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  resolution_note?: string | null;
};

export type WriteDown = {
  captured_hours: number;
  billed_hours: number;
  note: string;
};

export type TimeEntry = {
  id: string;
  law_firm_id: string;
  user_id: string;
  individual_id?: string | null;
  foreign_national_id?: string | null;
  work_date: string;
  case_label: string;
  client_label: string;
  started_at?: string | null;
  ended_at?: string | null;
  duration_seconds_raw: number;
  duration_hours: number;
  narrative: string;
  billable: boolean;
  fee_arrangement: FeeArrangement;
  rate: number;
  rate_override?: number | null;
  amount: number;
  utbms_task?: string | null;
  utbms_activity?: string | null;
  origin: EntryOrigin;
  write_down?: WriteDown | null;
  status: EntryStatus;
  status_label: string;
  unreleased: boolean;
  effective_rate: number;
  released_at?: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
  return_note?: string | null;
  signal_ids: string[];
  candidates: MatchCandidate[];
  export_batch_id?: string | null;
  flags: EntryFlag[];
  tags: string[];
  activity?: ActivityRow[];
  history?: HistoryItem[];
  /** Single-entry reads: names for everyone in `history`. */
  history_names?: Record<string, string>;
  user_name?: string;
  created_at?: string | null;
  updated_at?: string | null;
};

// ---- tracker -------------------------------------------------------------

export type TrackerContext = {
  individual_id: string | null;
  foreign_national_id: string | null;
  /** Workspace page or open artifact kind: "workspace", "draft", "forms"… */
  surface: string | null;
  /** `kind:id` of the open artifact, when there is one. */
  artifact: string | null;
  title?: string | null;
};

export type BeatSegment = {
  context: TrackerContext;
  started_at: string;
  ended_at: string;
  active_seconds: number;
};

export type BeatsResponse = {
  accepted: number;
  signals: string[];
  labels?: Record<string, string>;
};

// ---- reads ---------------------------------------------------------------

export type TimeTotals = {
  count: number;
  hours: number;
  released_hours: number;
  unreleased_count: number;
  hourly_value: number;
  needs_case: number;
};

export type EntriesResponse = {
  entries: TimeEntry[];
  totals: TimeTotals;
  /** Today in the person's own timezone. */
  today: string;
};

export type UnreleasedDay = {
  work_date: string;
  weekday: string;
  count: number;
  hours: number;
  /** Entries with a case, so releasable. */
  ready: number;
};

export type RunningTimer = {
  started_at: string;
  individual_id?: string | null;
  narrative?: string | null;
};

export type TodaySummary = {
  today: string;
  evening_review: boolean;
  /** "HH:MM", the user's local time. */
  review_time: string;
  /** Today's review time has passed. */
  review_open: boolean;
  totals: TimeTotals;
  unreleased_today: number;
  earlier_unreleased: UnreleasedDay[];
  paused: boolean;
  first_run_seen: boolean;
  timer: RunningTimer | null;
  timezone: string;
  /** An admin asked this person to release their time. */
  nudge?: TimeNudge | null;
};

export type TimeNudge = {
  date: string;
  by_name: string;
  count: number;
  oldest: string;
};

export type SignalRow = ActivityRow & { repeats?: number };

export type CaseOption = {
  individual_id: string;
  foreign_national_id?: string | null;
  label: string;
  employer?: string;
  /** ISO date the case was opened. */
  opened?: string | null;
};

export type LearnedPreference = {
  id: string;
  kind: string;
  value: string;
  individual_id: string;
  label: string;
  source?: string;
  hits?: number;
  created_at?: string | null;
};

export type TimeStatus = { enabled: boolean; is_admin: boolean };

// ---- the evening brief ---------------------------------------------------

export type SuggestionKind =
  | "assign_case"
  | "merge"
  | "rewrite_narrative"
  | "set_hours";

export type BriefSuggestion = {
  id: string;
  kind: SuggestionKind;
  entry_ids: string[];
  label: string;
  individual_id?: string;
  case_label?: string;
  narrative?: string;
  hours?: number;
  note?: string;
};

export type BriefTotals = {
  hours: number;
  entries: number;
  cases: number;
  unreleased: number;
  unreleased_hours: number;
  needs_case: number;
  ready: number;
};

export type TimeBrief = {
  work_date: string;
  weekday: string;
  summary: string;
  by_model: boolean;
  totals: BriefTotals;
  suggestions: BriefSuggestion[];
  learned: { id: string; label: string; source: string }[];
  entries: TimeEntry[];
  review_open: boolean;
  review_time: string;
  carry_over: UnreleasedDay[];
};

// ---- settings ------------------------------------------------------------

export type MyTimeSettings = {
  user_id: string;
  email_capture: boolean;
  calendar_capture: boolean;
  never_capture: string[];
  work_hours_only: boolean;
  work_hours_start: string;
  work_hours_end: string;
  evening_review: boolean;
  evening_review_time: string;
  morning_reminder: boolean;
  timezone: string;
  match_preferences: LearnedPreference[];
  first_run_seen: boolean;
  paused: boolean;
  timer?: RunningTimer | null;
  firm: {
    sources: Record<string, boolean>;
    idle_seconds: number;
    idle_document_seconds: number;
    agent_work: AgentWorkPolicy;
  };
  connections: { email?: boolean; calendar?: boolean };
};

export type MyTimeSettingsPatch = Partial<
  Pick<
    MyTimeSettings,
    | "email_capture"
    | "calendar_capture"
    | "never_capture"
    | "work_hours_only"
    | "work_hours_start"
    | "work_hours_end"
    | "evening_review"
    | "evening_review_time"
    | "morning_reminder"
    | "timezone"
    | "first_run_seen"
    | "paused"
  >
>;

export type AgentWorkPolicy = "context_only" | "suggest_review_time";

export type TimekeeperClass = "PT" | "AS" | "PL" | "OT" | "LA" | "SE";

export type Timekeeper = {
  uid: string;
  user_id: string;
  name?: string;
  email?: string;
  role?: string;
  rate?: number | null;
  classification?: TimekeeperClass | "" | null;
  timekeeper_id?: string | null;
};

export type FirmTimeSettings = {
  law_firm_id: string;
  enabled: boolean;
  enabled_at?: string | null;
  rounding_minutes: number;
  minimum_minutes: number;
  idle_seconds: number;
  idle_document_seconds: number;
  sources: Record<string, boolean>;
  private_until_released: boolean;
  require_approval: boolean;
  agent_work: AgentWorkPolicy;
  review_time_minutes: number;
  default_fee_arrangement: FeeArrangement;
  default_rate: number;
  utbms_task_codes: boolean;
  firm_ledes_id?: string | null;
  /** The firm's own billing rules, checked in pre-bill on every case. */
  rules?: BillingRule[];
};

export type FirmTimeSettingsPatch = Partial<
  Omit<FirmTimeSettings, "law_firm_id" | "enabled_at">
>;

// ---- a case --------------------------------------------------------------

export type TimeMatter = {
  individual_id: string;
  fee_arrangement?: FeeArrangement | null;
  flat_fee?: number | null;
  fee_cap?: number | null;
  rate_override?: number | null;
  rounding_minutes?: number | null;
  client_no?: string | null;
  matter_no?: string | null;
  company_id?: string | null;
  client_matter_id?: string | null;
  po?: string | null;
  cost_center?: string | null;
  employer_share?: number | null;
  /** Follows the company's fee schedule; false once the case sets its own fee. */
  company_terms?: boolean | null;
};

export type CaseTimeSummary = {
  individual_id: string;
  label: string;
  matter: TimeMatter;
  fee_arrangement: FeeArrangement;
  /** Released team time. */
  team_hours: number;
  /** Released team time plus your own unreleased time. */
  total_hours: number;
  my_unreleased_hours: number;
  team_amount: number;
  /** Counted hours at each timekeeper's standard rate, whatever the fee. */
  standard_value: number;
  effective_rate: number | null;
  average_for_case_type: number | null;
  by_timekeeper: {
    user_id: string;
    name: string;
    hours: number;
    amount: number;
    unreleased_hours: number;
  }[];
  mine: TimeEntry[];
  /** Everyone's released entries and all of yours, newest first. */
  entries: TimeEntry[];
};

// ---- Expenses --------------------------------------------------------------

export type ExpenseKind =
  | "filing_fee"
  | "premium_processing"
  | "recruitment_ad"
  | "courier"
  | "translation"
  | "other";

/** `suggested` comes from a filed receipt or the assistant; staff confirm it. */
export type ExpenseStatus =
  | "suggested"
  | "draft"
  | "approved"
  | "exported"
  | "discarded";

/** `split` follows the case's employer share. */
export type ExpensePayer = "split" | "employer" | "employee";

export type TimeExpense = {
  id: string;
  individual_id: string;
  case_label: string;
  date: string;
  kind: ExpenseKind;
  kind_label: string;
  description: string;
  amount: number;
  utbms_expense?: string;
  code_label: string;
  payer: ExpensePayer;
  employee_benefit: boolean;
  receipt_node_id?: string;
  origin: "manual" | "assistant" | "receipt";
  status: ExpenseStatus;
  approved_at?: string;
  export_batch_id?: string;
  /** The company's percent; null on a case with no company. */
  employer_pct: number | null;
  created_by?: string;
  updated_at?: string;
};

export type NewExpense = {
  kind: ExpenseKind;
  amount: number;
  date?: string;
  description?: string;
  payer?: ExpensePayer;
  employee_benefit?: boolean;
  utbms_expense?: string;
};

export type ExpenseFilters = {
  status?: ExpenseStatus[];
  from?: string;
  to?: string;
  client?: string;
};

// ---- Billing review ------------------------------------------------------

export type ReviewKpis = {
  awaiting_approval: { count: number; hours: number };
  ready_to_export: {
    count: number;
    hours: number;
    amount: number;
    expense_count?: number;
    expense_amount?: number;
  };
  flat_fee_tracked: { count: number; hours: number };
  blocking_issues: number;
  blocking?: { matters: number; timekeepers: number; flagged: number };
  require_approval: boolean;
};

export type ReviewFilters = {
  status?: EntryStatus[];
  from?: string;
  to?: string;
  user?: string;
  client?: string;
  fee?: FeeArrangement;
  q?: string;
};

export type MatterRow = TimeMatter & {
  label: string;
  client_label: string;
  client_matter_id_required: boolean;
  week_hours: number;
  fee_arrangement: FeeArrangement;
};

export type ExportFormat = "csv" | "xlsx" | "ledes_1998b" | "ledes_1998bi";

export type ExportRequest = {
  format: ExportFormat;
  from?: string;
  to?: string;
  client?: string;
  include_released?: boolean;
  /** Entries and expenses left out. */
  exclude_ids?: string[];
};

export type ExportIssueKind =
  | "matter_no"
  | "client_no"
  | "rate"
  | "classification"
  | "timekeeper_id"
  | "firm_ledes_id"
  | "client_matter_id"
  | "po"
  | "employee_client_no"
  | "no_approved_rate"
  | "pending_rate"
  | "above_approved_rate"
  | "ebilling_grouping"
  | "fee_cap"
  | "company_format";

export type ExportIssue = {
  kind: ExportIssueKind | string;
  /** The case, timekeeper or firm the fix goes on. */
  id: string;
  label: string;
  message: string;
  severity: "error" | "warn";
  entry_ids?: string[];
  /** A company check's one-click fix. */
  fix?: { action: "leave_out" | "price_at_approved_rate"; label: string };
};

/** A company's terms for the export dialog's first step. */
export type ExportCompanyTerms = {
  id: string;
  name: string;
  format: ExportFormat;
  format_label: string;
  grouping: "matter" | "entity";
  ebilling_system: string | null;
  require_client_matter_id: boolean;
  require_po: boolean;
  cases: number;
};

export type ExportPreview = {
  format: ExportFormat;
  count: number;
  hours: number;
  amount: number;
  included_released: boolean;
  issues: ExportIssue[];
  blocking: boolean;
  breakdown: {
    hourly: number;
    flat_fee: number;
    missing_activity: number;
    invoices: number;
    lines: number;
    expenses?: number;
  };
  expense_count?: number;
  expense_amount?: number;
  sample: string[];
  invoices: {
    number: string;
    label: string;
    matter_no: string;
    payer: string;
    lines: number;
    total: number;
  }[];
  flat_fee_matters: string[];
  companies?: ExportCompanyTerms[];
};

export type ExportBatch = {
  id: string;
  /** Short reference used on invoice numbers and the filename. */
  ref: string;
  format: ExportFormat;
  format_label: string;
  filters: { date_from?: string; date_to?: string; individual_id?: string };
  /** The first and last work date in the file. */
  work_dates?: string[];
  filename: string;
  content_type: string;
  line_count: number;
  entry_count: number;
  expense_count?: number;
  matter_count: number;
  total_hours: number;
  total_amount: number;
  included_released: boolean;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  voided: boolean;
  voided_at?: string;
};

export type WaitingRow = {
  user_id: string;
  name: string;
  count: number;
  days: number;
  oldest: string;
  nudged_today?: boolean;
};

// ---- pre-bill ----------------------------------------------------------------

export type PrebillCounts = {
  entries: number;
  released: number;
  approved: number;
  hours: number;
  value: number;
  /** Open flags that block approval. */
  errors: number;
  warnings: number;
  flagged_entries: number;
};

export type PrebillMatterRow = PrebillCounts & {
  individual_id: string;
  label: string;
  client_label: string;
  /** Nothing left to approve. */
  done: boolean;
};

export type BilledTo = {
  label: string;
  company_id: string | null;
  company_name?: string;
  share: number | null;
};

/** One matter: the counts, with `entries` as the rows rather than their count. */
export type PrebillMatter = Omit<PrebillCounts, "entries"> & {
  individual_id: string;
  foreign_national_id?: string | null;
  label: string;
  client_label: string;
  fee_arrangement: string;
  flat_fee?: number | null;
  fee_cap?: number | null;
  billed_to: BilledTo;
  entries: TimeEntry[];
  people: Record<string, string>;
  rules: AppliedRule[];
  guidelines: TimeGuideline[];
};

export type FlagAction = "fix" | "unfix" | "dismiss" | "undismiss" | "return";

// ---- billing rules and client guidelines ---------------------------------

export type RuleKind =
  | "block_billing"
  | "vague_narrative"
  | "clerical"
  | "internal_conference"
  | "rate_cap"
  | "hours_threshold"
  | "cost_approval"
  | "custom";

export type RuleSeverity = "error" | "warn";

export type RuleParams = {
  max_rate?: number;
  max_hours?: number;
  amount?: number;
  classification?: string;
  activity?: string;
  expense_kind?: string;
  needs?: string;
  /** hours_threshold: each entry alone, or the matter's running total (the default). */
  per?: "entry" | "matter";
};

/** A firm rule or one read from a client's guidelines. */
export type BillingRule = {
  id: string;
  section?: string;
  label: string;
  kind: RuleKind;
  params: RuleParams;
  severity: RuleSeverity;
  enabled: boolean;
  quote?: string;
  kind_label: string;
  /** Code checks numbers; the model judges narratives. */
  check: "code" | "model";
  summary: string;
};

export type RuleDraft = BillingRule & { guessed: boolean };

export type RuleSource = "firm" | "guideline";

/** A rule as it reaches one case, and whether it is on there. */
export type AppliedRule = BillingRule & {
  source: RuleSource;
  source_id: string;
  source_label: string;
  on: boolean;
};

export type CaseRules = { rules: AppliedRule[]; can_edit: boolean };

export type GuidelineKind = "guidelines" | "engagement_letter";
export type GuidelineStatus = "extracting" | "ready" | "failed";

export type TimeGuideline = {
  id: string;
  kind: GuidelineKind;
  title: string;
  filename: string;
  content_type: string;
  status: GuidelineStatus;
  error?: string;
  /** No model answered; the admin adds the rules by hand. */
  guessed: boolean;
  chars?: number;
  company_id?: string;
  foreign_national_id?: string;
  uploaded_by?: string;
  created_at?: string;
  updated_at?: string;
  rules: BillingRule[];
  rule_count: number;
  on_count: number;
};

/** Guidelines belong to a company or to an individual client. */
export type GuidelineOwner =
  | { company_id: string; foreign_national_id?: undefined }
  | { foreign_national_id: string; company_id?: undefined };
